/**
 * ProjectStackBar — Secondary "active mandate / engagement / mission" context bar.
 *
 * Renders only when an active project is selected. Carries:
 *  - Project icon + label + type chip (mandate / engagement / mission)
 *  - Step progress (mini stepper) — for auditor/consultant flows
 *  - Quick tabs (Overview / Documents / Findings / Team / Settings)
 *  - Close (deselect) button
 *
 * Designed to sit immediately below the TopBar, above page content. Sticky.
 */

import { Link, useLocation } from 'react-router-dom'
import {
  FolderOpen, FileText, ListChecks, Users, Settings, X, Check,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

export interface ProjectStackTab {
  id: string
  label: string
  icon: LucideIcon
  href: string
}

export interface ProjectStackStep {
  id: string
  label: string
  done: boolean
}

interface ProjectStackBarProps {
  title: string
  type?: string
  icon?: LucideIcon
  accent?: 'blue' | 'amber' | 'green' | 'purple' | 'red' | 'teal' | 'indigo' | 'slate'
  progress?: number
  steps?: ProjectStackStep[]
  tabs?: ProjectStackTab[]
  onClose?: () => void
}

const ACCENT_BG: Record<NonNullable<ProjectStackBarProps['accent']>, string> = {
  blue: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
  amber: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  green: 'bg-green-500/15 text-green-600 dark:text-green-400',
  purple: 'bg-purple-500/15 text-purple-600 dark:text-purple-400',
  red: 'bg-red-500/15 text-red-600 dark:text-red-400',
  teal: 'bg-teal-500/15 text-teal-600 dark:text-teal-400',
  indigo: 'bg-indigo-500/15 text-indigo-600 dark:text-indigo-400',
  slate: 'bg-slate-500/15 text-slate-600 dark:text-slate-400',
}

export const DEFAULT_PROJECT_TABS: ProjectStackTab[] = [
  { id: 'overview', label: 'Aperçu', icon: FolderOpen, href: '/mandats' },
  { id: 'docs', label: 'Documents', icon: FileText, href: '/global/documents' },
  { id: 'tasks', label: 'Tâches', icon: ListChecks, href: '/global/tasks' },
  { id: 'team', label: 'Équipe', icon: Users, href: '/team' },
  { id: 'settings', label: 'Paramètres', icon: Settings, href: '/settings/profile' },
]

export function ProjectStackBar({
  title,
  type,
  icon: Icon = FolderOpen,
  accent = 'blue',
  progress,
  steps,
  tabs = DEFAULT_PROJECT_TABS,
  onClose,
}: ProjectStackBarProps) {
  const location = useLocation()

  const computedProgress =
    typeof progress === 'number'
      ? Math.max(0, Math.min(100, progress))
      : steps && steps.length > 0
        ? Math.round((steps.filter((s) => s.done).length / steps.length) * 100)
        : undefined

  return (
    <div
      role="region"
      aria-label="Active project"
      className={cn(
        'shrink-0 sticky top-0 z-30',
        'flex items-center gap-3 px-4 h-10',
        'border-b bg-card/90 backdrop-blur-sm',
      )}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-lg shrink-0',
            ACCENT_BG[accent],
          )}
        >
          <Icon className="h-3.5 w-3.5" />
        </span>
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[13px] font-semibold text-foreground truncate max-w-[220px]">
            {title}
          </span>
          {type && (
            <Badge
              variant="outline"
              className="h-5 px-1.5 text-[10px] font-medium shrink-0"
            >
              {type}
            </Badge>
          )}
        </div>
      </div>

      {steps && steps.length > 0 && (
        <ol className="hidden md:flex items-center gap-1 shrink-0" aria-label="Steps">
          {steps.map((step, idx) => (
            <li key={step.id} className="flex items-center gap-1">
              <span
                className={cn(
                  'flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold',
                  step.done
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground',
                )}
                title={step.label}
              >
                {step.done ? <Check className="h-2.5 w-2.5" /> : idx + 1}
              </span>
              {idx < steps.length - 1 && (
                <span
                  className={cn(
                    'h-px w-3',
                    step.done ? 'bg-primary' : 'bg-muted',
                  )}
                />
              )}
            </li>
          ))}
        </ol>
      )}

      {!steps && typeof computedProgress === 'number' && (
        <div className="hidden sm:flex items-center gap-2 shrink-0 w-40">
          <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${computedProgress}%` }}
            />
          </div>
          <span className="text-[10px] font-bold text-muted-foreground tabular-nums">
            {computedProgress}%
          </span>
        </div>
      )}

      <nav
        aria-label="Project sections"
        className="flex-1 flex items-center gap-0.5 overflow-x-auto scrollbar-none min-w-0 justify-end"
      >
        {tabs.map((tab) => {
          const isActive =
            location.pathname === tab.href ||
            location.pathname.startsWith(tab.href + '/')
          const TabIcon = tab.icon
          return (
            <Link
              key={tab.id}
              to={tab.href}
              className={cn(
                'flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium whitespace-nowrap',
                'transition-colors',
                isActive
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              <TabIcon className="h-3 w-3" />
              <span className="hidden sm:inline">{tab.label}</span>
            </Link>
          )
        })}
      </nav>

      {onClose && (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={onClose}
          aria-label="Close project"
          title="Désactiver le mandat"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  )
}
